import React, { useEffect, useState } from "react";

export class ImagesDatabase {
  private _clearImage(url: string) {
    // logDebug('Clearing image from cache:', url);
    delete this._images[url];
    delete this._cacheTime[url];
  }
  private static instance: ImagesDatabase;
  private constructor() {
    // Private constructor to prevent instantiation
  }
  public static getInstance() {
    if (!ImagesDatabase.instance) {
      ImagesDatabase.instance = new ImagesDatabase();
    }
    return ImagesDatabase.instance;
  }

  private _lastScanTime: number = 0;
  private _images: { [key: string]: any } = {};
  private _cacheTime: { [key: string]: number } = {};

  async getImage(url: string): Promise<typeof Image> {
    const retImage = this._images[url];
    if (retImage) {
      this._cacheTime[url] = Date.now();
    }

    if (Date.now() - this._lastScanTime > 1000 * 60 * 15) {
      this._doScan();
    }

    if (retImage) {
      return retImage;
    }

    return new Promise<any>((resolve, reject) => {
      const image = new Image();

      image.crossOrigin = "Anonymous";
      image.onload = () => {
        // logDebug('Image loaded to cache:', url);
        this._images[url] = image;
        this._cacheTime[url] = Date.now();
        resolve(image);
      };
      image.onerror = (err) => {
        reject(err);
      };
      image.src = url;
    });
  }
  private _doScan() {
    const now = Date.now();
    for (const key in this._cacheTime) {
      if (now - (this._cacheTime[key] || 0) > 1000 * 60 * 30) {
        this._clearImage(key);
      }
    }
    this._lastScanTime = now;
  }
}

const CroppedBoundingBoxImage: React.FC<{
  className?: string;
  paddingPixels?: number | { vertical: number; horizontal: number };
  url: string;
  rect?: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  box?: boolean;
  outputWidth?: number;
  outputHeight?: number;
}> = ({ className, url, rect, paddingPixels = 0, box, outputWidth, outputHeight }) => {
  // Parse paddingPixels into horizontal and vertical values
  const padding = typeof paddingPixels === "number" ? { horizontal: paddingPixels, vertical: paddingPixels } : paddingPixels;

  const [croppedImageUrl, setCroppedImageUrl] = useState("");

  async function setCanvas() {
    try {
      const image = (await ImagesDatabase.getInstance().getImage(url)) as unknown as HTMLImageElement;

      // If no rect is provided, use the full image
      if (!rect) {
        const canvas = document.createElement("canvas");
        const canvasWidth = outputWidth ?? image.width;
        const canvasHeight = outputHeight ?? image.height;

        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        const ctx: any = canvas.getContext("2d");

        // Draw the full image, scaled to canvas dimensions
        ctx.drawImage(image, 0, 0, canvasWidth, canvasHeight);

        // Convert the canvas to a data URL
        const dataUrl = canvas.toDataURL();
        setCroppedImageUrl(dataUrl);
        return;
      }

      // Extract left, top, width, and height from rect
      const { left, top, width: rectWidth, height: rectHeight } = rect;

      // Store original dimensions for box rendering
      const originalWidth = rectWidth;
      const originalHeight = rectHeight;

      // Calculate the desired crop region with padding
      const desiredLeft = left - padding.horizontal;
      const desiredTop = top - padding.vertical;
      const desiredWidth = rectWidth + padding.horizontal * 2;
      const desiredHeight = rectHeight + padding.vertical * 2;

      // Calculate what we can actually crop from the image
      const cropLeft = Math.max(0, desiredLeft);
      const cropTop = Math.max(0, desiredTop);
      const cropRight = Math.min(image.width, desiredLeft + desiredWidth);
      const cropBottom = Math.min(image.height, desiredTop + desiredHeight);
      const cropWidth = cropRight - cropLeft;
      const cropHeight = cropBottom - cropTop;

      // Calculate offsets for positioning the cropped portion on the canvas
      const offsetLeft = cropLeft - desiredLeft;
      const offsetTop = cropTop - desiredTop;

      // Use outputWidth/outputHeight if provided, otherwise use desired region size
      const canvasWidth = outputWidth ?? desiredWidth;
      const canvasHeight = outputHeight ?? desiredHeight;

      const canvas = document.createElement("canvas");
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      const ctx: any = canvas.getContext("2d");

      // Fill the entire canvas with black (this creates the buffer space)
      ctx.fillStyle = "black";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Calculate scaling factors
      const scaleX = canvasWidth / desiredWidth;
      const scaleY = canvasHeight / desiredHeight;

      // Draw the cropped portion of the image at the correct position
      if (cropWidth > 0 && cropHeight > 0) {
        ctx.drawImage(
          image,
          cropLeft,
          cropTop,
          cropWidth,
          cropHeight, // Source rectangle
          offsetLeft * scaleX,
          offsetTop * scaleY,
          cropWidth * scaleX,
          cropHeight * scaleY, // Destination rectangle
        );
      }

      // Draw blue box overlay if box prop is true
      if (box) {
        ctx.strokeStyle = "rgba(0, 0, 255, 0.7)";
        ctx.lineWidth = 2;
        ctx.strokeRect(padding.horizontal * scaleX, padding.vertical * scaleY, originalWidth * scaleX, originalHeight * scaleY);
      }

      // Convert the canvas to a data URL
      const dataUrl = canvas.toDataURL();

      // Set the data URL to the state
      setCroppedImageUrl(dataUrl);
    } catch (err) {
      console.log(err);
      // logError('Error loading image:', err);
    }
  }

  useEffect(() => {
    setCanvas();
  }, [url, rect, box, outputWidth, outputHeight, paddingPixels]);

  if (!croppedImageUrl) {
    return null;
  }

  return (
    <img
      src={croppedImageUrl}
      alt="Cropped Image"
      className={className ?? ""}
      style={outputWidth && outputHeight ? { width: outputWidth, height: outputHeight } : {}}
    />
  );
};

const BoundingBoxImage: React.FC<{
  className?: string;
  url: string;
  rect?: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  box?: boolean;
  boxColor?: string;
  showFill?: boolean;
  outputWidth?: number;
  outputHeight?: number;
}> = ({ className, url, rect, box, boxColor = "rgba(0, 0, 255, 0.7)", showFill = true, outputWidth, outputHeight }) => {
  const [canvasImageUrl, setCanvasImageUrl] = useState("");

  async function setCanvas() {
    try {
      const image = (await ImagesDatabase.getInstance().getImage(url)) as unknown as HTMLImageElement;

      // Use outputWidth/outputHeight if provided, otherwise use image dimensions
      const canvasWidth = outputWidth ?? image.width;
      const canvasHeight = outputHeight ?? image.height;

      const canvas = document.createElement("canvas");
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      const ctx: any = canvas.getContext("2d");

      // Fill with black first (in case of scaling that creates empty space)
      ctx.fillStyle = "black";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw the full image, scaling if needed
      ctx.drawImage(image, 0, 0, image.width, image.height, 0, 0, canvasWidth, canvasHeight);

      // Draw box overlay if box prop is true
      if (rect && box && rect.width > 0 && rect.height > 0) {
        // Scale box to output size
        const scaleX = canvasWidth / image.width;
        const scaleY = canvasHeight / image.height;

        // Ensure the box stays within canvas bounds
        const boxLeft = Math.max(0, Math.min(canvasWidth - 2, rect.left * scaleX));
        const boxTop = Math.max(0, Math.min(canvasHeight - 2, rect.top * scaleY));
        const boxWidth = Math.min(rect.width * scaleX, canvasWidth - boxLeft);
        const boxHeight = Math.min(rect.height * scaleY, canvasHeight - boxTop);

        if (boxWidth > 0 && boxHeight > 0) {
          // Draw semi-transparent fill if showFill is true
          if (showFill) {
            ctx.fillStyle = boxColor.replace(/[\d.]+\)$/, "0.3)"); // Change opacity to 0.3 for fill
            ctx.fillRect(boxLeft, boxTop, boxWidth, boxHeight);
          }

          // Draw stroke on top
          ctx.strokeStyle = boxColor;
          ctx.lineWidth = 3;
          ctx.strokeRect(boxLeft, boxTop, boxWidth, boxHeight);
        }
      }

      // Convert the canvas to a data URL
      const dataUrl: any = canvas.toDataURL();

      // Set the data URL to the state
      setCanvasImageUrl(dataUrl);
    } catch (err) {
      console.log(err);
      // logError('Error loading image:', err);
    }
  }

  useEffect(() => {
    setCanvas();
  }, [url, rect, box, boxColor, showFill, outputWidth, outputHeight]);

  if (!canvasImageUrl) {
    return null;
  }

  return (
    <img
      src={canvasImageUrl}
      alt="Image with Overlay"
      className={className ?? ""}
      style={outputWidth && outputHeight ? { width: outputWidth, height: outputHeight } : {}}
    />
  );
};

const ImageCanvas: React.FC<{
  className?: string;
  paddingPixels?: number | { vertical?: number; horizontal?: number };
  url: string;
  rect: {
    x?: number;
    y?: number;
    left?: number;
    top?: number;
    width: number;
    height: number;
  };
}> = ({ className, url, rect, paddingPixels }) => {
  // Destructure the rect object to extract x, y, width, and height
  // logDebug('ImageCanvas', { url, rect });
  rect = rect || { x: 0, y: 0, width: 0, height: 0 };
  let { x, y, width, height } = rect;
  const { left, top } = rect;
  x = x || left || 0;
  y = y || top || 0;

  // Handle different padding formats
  let horizontalPadding = 0;
  let verticalPadding = 0;

  if (typeof paddingPixels === "number") {
    horizontalPadding = paddingPixels;
    verticalPadding = paddingPixels;
  } else if (paddingPixels && typeof paddingPixels === "object") {
    horizontalPadding = paddingPixels.horizontal || 0;
    verticalPadding = paddingPixels.vertical || 0;
  }

  width = width + horizontalPadding * 2;
  height = height + verticalPadding * 2;
  x = x - horizontalPadding;
  y = y - verticalPadding;
  if (x < 0) x = 0;
  if (y < 0) y = 0;

  const [croppedImageUrl, setCroppedImageUrl] = useState("");

  async function setCanves() {
    try {
      const image = (await ImagesDatabase.getInstance().getImage(url)) as unknown as HTMLImageElement;
      if (rect.width === 0 && rect.height === 0) {
        width = image.width;
        height = image.height;
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx: any = canvas.getContext("2d");

      // Draw the desired region of the image onto the canvas
      ctx.drawImage(
        image,
        x,
        y,
        width,
        height, // Source rectangle
        0,
        0,
        width,
        height, // Destination rectangle
      );

      // Convert the canvas to a data URL
      const dataUrl: any = canvas.toDataURL();

      // Set the data URL to the state
      setCroppedImageUrl(dataUrl);
    } catch (err) {
      console.log(err);
      // logError('Error loading image:', err);
    }
  }
  useEffect(() => {
    setCanves();
  }, [url, x, y, width, height]);

  if (!croppedImageUrl) {
    return null;
  }
  return <img src={croppedImageUrl} alt="Cropped Image" className={className ?? ""} />;
};

export { ImageCanvas, CroppedBoundingBoxImage, BoundingBoxImage };
